// src/pages/login/LoginPage.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { loginThunk, clearError } from "../../store/slices/authSlice";
import logo from "../../assets/images/logo-toinup-horizontal.svg";
import illustration from "../../assets/images/login-illustration.png";
import { Alert } from "../../components/ui/Alert";

/**
 * Pantalla de login del panel administrativo.
 * Se ingresa con el usuario o el email y la contraseña de la app Toinup.
 * Solo cuentas activas con ROLE_ADMIN pueden acceder.
 */
export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, token } = useAppSelector((s) => s.auth);

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Si ya hay token, ir directo al dashboard
  useEffect(() => {
    if (token) navigate("/dashboard", { replace: true });
  }, [token, navigate]);

  // Limpiar error al desmontar
  useEffect(
    () => () => {
      dispatch(clearError());
    },
    [dispatch],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usernameOrEmail.trim() || !password) return;
    dispatch(loginThunk({ usernameOrEmail: usernameOrEmail.trim(), password }));
  };

  return (
    <div className="login-page">
      {/* Ilustración (igual que el Login de la app) */}
      <section className="login-hero">
        <img className="illustration" src={illustration} alt="" />
        <div className="hero-text">
          <h2>Panel Administrativo</h2>
          <p>
            Gestiona los usuarios de Toinup, sus cuentas y los cupones de
            suscripción.
          </p>
        </div>
      </section>

      {/* Formulario */}
      <section className="login-side">
        <div className="login-card">
          <div className="login-logo">
            <img src={logo} alt="Toinup" />
          </div>

          <h1 className="login-title">Login</h1>
          <p className="login-subtitle">Ingresa tu usuario y contraseña</p>

          {error && (
            <Alert
              type="error"
              message={error}
              onClose={() => dispatch(clearError())}
            />
          )}

          <form onSubmit={handleSubmit} noValidate>
            <label className="visually-hidden" htmlFor="login-user">
              Usuario o email
            </label>
            <div className="login-input">
              <i className="bi bi-person" />
              <input
                id="login-user"
                type="text"
                placeholder="Usuario o email"
                value={usernameOrEmail}
                onChange={(e) => setUsernameOrEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                required
              />
            </div>

            <label className="visually-hidden" htmlFor="login-password">
              Contraseña
            </label>
            <div className="login-input">
              <i className="bi bi-lock" />
              <input
                id="login-password"
                type={showPass ? "text" : "password"}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                tabIndex={-1}
                title={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                <i className={`bi ${showPass ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !usernameOrEmail.trim() || !password}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Verificando...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </button>
          </form>

          <div
            className="text-center mt-4"
            style={{ fontSize: "0.75rem", color: "var(--tp-muted)" }}
          >
            <i className="bi bi-lock-fill me-1" />
            Solo usuarios administradores activos pueden ingresar
          </div>
        </div>
      </section>
    </div>
  );
}
